import Link from 'next/link';
import { Button } from '@/app/components';
import './TopToolbar.scss';
import { useSite } from '@/lib/site-context';
import { isTauri } from '@/lib/runtime-bridge';

interface TopToolbarProps {
  sketchTitle?: string;
  onTitleChange?: (title: string) => void;
  onSave?: () => void;
  isSaving?: boolean;
  lastSaved?: Date | null;
  isAuthenticated?: boolean;
  isExample?: boolean;
  isViewOnly?: boolean;
  showForkButton?: boolean;
  onFork?: () => void;
  svgThumbnail?: string | null;
  sketchMeta?: {
    author?: string;
    authorUsername?: string;
    views?: number;
    createdAt?: string;
    description?: string;
  };
  hasControls?: boolean;
  sketchId?: string;
}

export default function TopToolbar({
  sketchTitle,
  onTitleChange,
  onSave,
  isSaving = false,
  lastSaved,
  isAuthenticated = false,
  isExample = false,
  isViewOnly = false,
  showForkButton = false,
  onFork,
  svgThumbnail,
  sketchMeta,
  hasControls = false,
  sketchId,
}: TopToolbarProps) {
  const site = useSite(); return (
    <div className="top-toolbar">
      <div className="toolbar-left">
        <Link href="/" className="logo-link">
          <h1>{'<'} {site.displayName}</h1>
        </Link>
        {sketchTitle !== undefined && (
          <div className="sketch-title-container">
            {/* SVG Thumbnail */}
            {svgThumbnail && (
              <div className="sketch-thumbnail">
                <div
                  className="thumbnail-svg"
                  dangerouslySetInnerHTML={{ __html: svgThumbnail }}
                />
              </div>
            )}
            {/* Show editable input for create page, read-only title for others */}
            {onTitleChange && !isViewOnly ? (
              <input
                type="text"
                value={sketchTitle}
                onChange={(e) => onTitleChange(e.target.value)}
                className="sketch-title-input"
                placeholder="Untitled Sketch"
              />
            ) : (
              <h2 className="sketch-title-display">{sketchTitle}</h2>
            )}
            {/* Show sketch metadata for view pages */}
            {sketchMeta && (
              <div className="sketch-meta">
                {sketchMeta.author && (
                  <span className="author">
                    by{' '}
                    {sketchMeta.authorUsername ? (
                      <Link href={`/u/${sketchMeta.authorUsername}`}>
                        {sketchMeta.authorUsername}
                      </Link>
                    ) : (
                      sketchMeta.author
                    )}
                  </span>
                )}
                {sketchMeta.views !== undefined && (
                  <span className="views">👁️ {sketchMeta.views}</span>
                )}
                {sketchMeta.createdAt && (
                  <span className="created">
                    Created {new Date(sketchMeta.createdAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="toolbar-right">
        {/* Action buttons for view pages */}
        {showForkButton && onFork && (
          <div className="view-actions">
            <Button onClick={onFork} className="fork-btn" type="outline">
              Fork This Sketch
            </Button>
          </div>
        )}

        {/* Save section for editable pages */}
        {onSave && (
          <div className="save-section">
            {sketchId && !isExample && (
              isTauri() ? (
                <Link
                  href={`/id/${sketchId}/view`}
                  className="view-btn"
                >
                  View ↗
                </Link>
              ) : (
                <a
                  href={`/id/${sketchId}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-btn"
                >
                  View ↗
                </a>
              )
            )}
            {isAuthenticated ? (
              <Button
                onClick={onSave}
                disabled={isSaving}
                className="save-btn"
              >
                {isExample ? (isSaving ? 'Saving as Sketch...' : 'Save as Sketch') : (isSaving ? 'Saving...' : 'Save')}
              </Button>
            ) : isTauri() ? (
              <Button onClick={() => alert('Local save coming soon')} className="save-btn">
                Save to Disk
              </Button>
            ) : (
              <Button href="/auth/signin" className="save-btn disabled">
                Sign in to Save
              </Button>
            )}
            {lastSaved && (
              <span className="last-saved">
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
